import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { studentsApi, configApi, callLogsApi, familiesApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MultiSelect } from '../components/MultiSelect';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { BulkSelectionBar } from '../components/BulkDelete';
import {
  Search, Plus, ChevronLeft, ChevronRight, User, Loader2,
  Pencil, Users, X, Power, Trash2, Phone, ArrowUp, ArrowDown, ArrowUpDown,
  LogOut, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '../components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';

// Holding class for students admitted before their class is decided. Must match
// NOT_ASSIGNED_CLASS in backend/config.py — the backend enforces the edit rule
// that hangs off it.
const NOT_ASSIGNED_CLASS = 'Not Assigned';

const formatCurrency = (amount, currency = 'USD') => {
  if (!amount && amount !== 0) return '-';
  return `${currency} ${Number(amount).toLocaleString()}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const StudentsPage = () => {
  const navigate = useNavigate();
  const { isAdmin, userBranches, role } = useAuth();
  
  // Full student management (edit anyone, bulk actions, deactivate, delete).
  // Mirrors STUDENT_EDIT_ANY_ROLES on the backend. cc.xlsx June-11 asked for
  // managers to have "Add Student"; the same gate also exposes Edit.
  const canManageStudents = isAdmin || ['manager', 'accountant'].includes(role);

  // Add-only roles. They may register a student and keep correcting that record
  // while it sits in the "Not Assigned" holding class, but not once a real class
  // has been assigned. Mirrors STUDENT_EDIT_UNASSIGNED_ONLY_ROLES on the backend.
  const canEditUnassignedOnly = ['accountant_assistant', 'admission_incharge'].includes(role);

  const canAddStudent = canManageStudents || canEditUnassignedOnly;

  // Per-row edit gate — the backend enforces the same rule, this only decides
  // whether the pencil is offered.
  const canEditStudent = (student) => canManageStudents || (
    canEditUnassignedOnly &&
    (student?.current_class || '').trim() === NOT_ASSIGNED_CLASS
  );

  // Roles permitted to see the Effective Outstanding column / Invoice Outstanding KPI.
  const canViewOutstanding = ['admin', 'manager', 'accountant', 'accountant_assistant'].includes(role);
  
  // Get available branches for this user
  const getAvailableBranches = (configBranches) => {
    if (isAdmin) return configBranches || [];
    return userBranches || [];
  };
  
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState([]);

  // Column sort state — null means insertion order (the API default).
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // Click → asc; click again → desc; click third time → clear.
  // Server-side sort runs across all matching students (not just the visible page).
  const handleSort = (field) => {
    setPage(1);  // jump to first page when sort changes
    if (sortField !== field) {
      setSortField(field); setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortField(null); setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40 inline" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 text-[#3E3F96] inline" />
      : <ArrowDown className="w-3 h-3 ml-1 text-[#3E3F96] inline" />;
  };
  const [classFilter, setClassFilter] = useState([]);
  
  // Add Student Dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newStudent, setNewStudent] = useState({
    first_name: '', last_name: '', branch: '', current_class: '',
    parent_guardian: '', email: '', phone: '', family_code: '', date_of_joining: ''
  });

  // Families (for linking a new student to a family). Only admin/manager can read /families.
  const canUseFamilies = isAdmin || role === 'manager';
  const [families, setFamilies] = useState([]);
  useEffect(() => {
    if (!canUseFamilies) return;
    familiesApi.getAll().then((r) => setFamilies(r.data || [])).catch(() => setFamilies([]));
  }, [canUseFamilies]);

  // When a family is chosen in the Add dialog, prefill branch + guardian from it.
  const onSelectFamily = (code) => {
    const fam = families.find((f) => f.family_code === code);
    setNewStudent((s) => ({
      ...s,
      family_code: code,
      // Auto-populate branch + guardian contact from the selected family.
      branch: fam?.branch || s.branch,
      parent_guardian: fam?.guardian_display_name || s.parent_guardian,
      email: fam?.guardian_display_email || s.email,
      phone: fam?.guardian_display_phone || s.phone,
    }));
  };

  // Edit Student Dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [editData, setEditData] = useState({});
  // Snapshot of the values the dialog opened with, so we can PUT only what the
  // user actually touched. Sending the whole form meant any field the list row
  // didn't carry got written back as blank/0.
  const [editOriginal, setEditOriginal] = useState({});

  // Bulk Edit
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({ branch: '', current_class: '' });
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  // Status filter
  const [statusFilter, setStatusFilter] = useState([]);

  // Lifecycle tabs: LIVE | QUALIFIED | LEFT. 'live' is the present (default) view.
  // ?tab=live|qualified|left (Dashboard quick-access) opens that tab directly.
  const [searchParams] = useSearchParams();
  const [lifecycleTab, setLifecycleTab] = useState(() => {
    const t = (searchParams.get('tab') || '').toLowerCase();
    return ['live', 'qualified', 'left'].includes(t) ? t : 'live';
  });
  // Term filter for the KPIs (Opening Balance + Net Invoice Outstanding). 'all' = no term.
  const [kpiTerm, setKpiTerm] = useState('all');
  const [lifecycleSubmitting, setLifecycleSubmitting] = useState(false);
  const canManageLifecycle = isAdmin || role === 'manager';

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Call Log
  const [callLogTarget, setCallLogTarget] = useState(null);
  const [callLogData, setCallLogData] = useState({ feedback: '', expected_payment_date: '', call_date: new Date().toISOString().split('T')[0] });
  const [savingCallLog, setSavingCallLog] = useState(false);

  // KPI Summary
  const [kpiSummary, setKpiSummary] = useState({
    totalStudents: 0,
    totalOpeningBalance: 0,
    totalInvoiceOutstanding: 0,
    totalOverdue: 0,
  });

  const fetchKpiSummary = useCallback(async () => {
    try {
      const params = { lifecycle: lifecycleTab };
      if (branchFilter.length) params.branch = branchFilter.join(',');
      // Send the status filter too, so the cards always describe the list below
      // them rather than a wider set.
      if (statusFilter.length) params.status = statusFilter.join(',');
      if (kpiTerm && kpiTerm !== 'all') params.term = kpiTerm;
      const response = await studentsApi.getKpiSummary(params);
      setKpiSummary({
        totalStudents: response.data.total_students,
        totalOpeningBalance: response.data.total_opening_balance,
        totalInvoiceOutstanding: response.data.total_invoice_outstanding,
        totalOverdue: response.data.total_overdue ?? 0,
      });
    } catch (err) {
      console.error('Failed to fetch KPI summary:', err);
    }
  }, [branchFilter, statusFilter, lifecycleTab, kpiTerm]);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await configApi.get();
      setConfig(response.data);
    } catch (err) {
      console.error('Failed to fetch config:', err);
    }
  }, []);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25, lifecycle: lifecycleTab };
      if (search) params.search = search;
      if (branchFilter.length) params.branch = branchFilter.join(',');
      if (classFilter.length) params.current_class = classFilter.join(',');
      if (statusFilter.length) params.status = statusFilter.join(',');
      if (sortField) {
        params.sort_by = sortField;
        params.sort_dir = sortDir;
      }

      const response = await studentsApi.getAll(params);
      setStudents(response.data.students);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (err) {
      toast.error('Failed to load students');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, branchFilter, classFilter, statusFilter, lifecycleTab, sortField, sortDir]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);
  useEffect(() => { fetchStudents(); }, [fetchStudents]);
  useEffect(() => { fetchKpiSummary(); }, [fetchKpiSummary]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchStudents();
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await studentsApi.create(newStudent);
      const applied = res?.data?.family_discounts_applied || 0;
      toast.success(applied > 0 ? `Student added; ${applied} family discount(s) sent for approval` : 'Student added successfully');
      setShowAddDialog(false);
      setNewStudent({ first_name: '', last_name: '', branch: '', current_class: '', parent_guardian: '', email: '', phone: '', family_code: '', date_of_joining: '' });
      fetchStudents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add student');
    } finally {
      setSubmitting(false);
    }
  };

  // Single Edit
  const openEditDialog = (student, e) => {
    e.stopPropagation();
    setEditingStudent(student);
    // Bind to the real student fields. parent_email/parent_phone are the actual
    // stored keys (the old form used non-existent email/phone, so edits never saved).
    const data = {
      first_name: student.first_name || '',
      last_name: student.last_name || '',
      branch: student.branch || '',
      current_class: student.current_class || '',
      parent_guardian: student.parent_guardian || '',
      // The contact email used for invoice/receipt emails is `parent_email`. Some
      // legacy records only have the student `email` field, so prefill from whichever
      // is present and always SAVE back into parent_email (the field sends read).
      parent_email: student.parent_email || student.email || '',
      parent_phone: student.parent_phone || '',
      // Admission date — editing it re-derives the joining term server-side.
      date_of_joining: student.date_of_joining || ''
    };
    // Read-only hint for the form; never sent back (it is not in editOriginal
    // as a comparable field because the backend owns it).
    data.joining_source = student.joining_source || '';
    // Admin-only fields: identity, academic year and discount percentages.
    if (isAdmin) {
      data.student_id = student.student_id || '';
      data.academic_year = student.academic_year || '';
      data.discount_ismaili = student.discount_ismaili ?? 0;
      data.discount_pak_assoc = student.discount_pak_assoc ?? 0;
      data.discount_special = student.discount_special ?? 0;
      data.discount_sibling = student.discount_sibling ?? 0;
      data.discount_yearly = student.discount_yearly ?? 0;
      data.discount_benefit = student.discount_benefit ?? 0;
    }
    setEditData(data);
    // The baseline is what is actually STORED. For a legacy record that only has
    // `email`, the stored parent_email is '' while the form shows the fallback —
    // so saving migrates it into parent_email instead of silently dropping it.
    setEditOriginal({ ...data, parent_email: student.parent_email || '' });
    setShowEditDialog(true);
  };

  const handleEditStudent = async (e) => {
    e.preventDefault();
    // Only send the fields that changed. An untouched field is never transmitted,
    // so it can't overwrite what's stored (blank contact details, 0% discounts).
    const changed = {};
    Object.keys(editData).forEach((k) => {
      if (editData[k] !== editOriginal[k]) changed[k] = editData[k];
    });
    if (Object.keys(changed).length === 0) {
      setShowEditDialog(false);
      setEditingStudent(null);
      return;
    }
    setSubmitting(true);
    try {
      await studentsApi.update(editingStudent.id, changed);
      toast.success('Student updated successfully');
      setShowEditDialog(false);
      setEditingStudent(null);
      fetchStudents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update student');
    } finally {
      setSubmitting(false);
    }
  };

  // Bulk Selection
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === students.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(students.map(s => s.id)));
    }
  };

  const changeLifecycleTab = (tab) => {
    if (tab === lifecycleTab) return;
    setLifecycleTab(tab);
    setSelectedIds(new Set());
    setPage(1);
  };

  const markLifecycle = async (targetLifecycle) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const labels = { left: 'LEFT', live: 'LIVE', qualified: 'QUALIFIED' };
    const verb = targetLifecycle === 'left' ? 'mark as LEFT' : `move to ${labels[targetLifecycle]}`;
    if (!window.confirm(`${ids.length} student(s) will ${verb}. Continue?`)) return;
    setLifecycleSubmitting(true);
    try {
      const res = await studentsApi.setLifecycle({ student_ids: ids, lifecycle: targetLifecycle });
      toast.success(res.data.message);
      setSelectedIds(new Set());
      fetchStudents();
      fetchKpiSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update students');
    } finally {
      setLifecycleSubmitting(false);
    }
  };

  const handleBulkEdit = async () => {
    const updates = {};
    if (bulkEditData.branch) updates.branch = bulkEditData.branch;
    if (bulkEditData.current_class) updates.current_class = bulkEditData.current_class;
    
    if (Object.keys(updates).length === 0) {
      toast.error('Please select at least one field to update');
      return;
    }
    
    setBulkSubmitting(true);
    try {
      const res = await studentsApi.bulkUpdate({
        student_ids: Array.from(selectedIds),
        updates
      });
      toast.success(res.data.message);
      setShowBulkEditDialog(false);
      setBulkEditData({ branch: '', current_class: '' });
      setSelectedIds(new Set());
      fetchStudents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to bulk update');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const getBalanceColor = (balance) => {
    if (balance > 0) return 'text-[#C85A40]';
    if (balance < 0) return 'text-[#5556A8]';
    return 'text-[#5C635F]';
  };

  const handleToggleStatus = async (student, e) => {
    e.stopPropagation();
    try {
      const res = await studentsApi.toggleStatus(student.id);
      toast.success(res.data.message);
      fetchStudents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle status');
    }
  };

  const handleDeleteStudent = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await studentsApi.delete(deleteTarget.id);
      toast.success('Student deleted successfully');
      setDeleteTarget(null);
      fetchStudents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete student');
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveCallLog = async (e) => {
    e.preventDefault();
    if (!callLogTarget) return;
    setSavingCallLog(true);
    try {
      await callLogsApi.create({
        student_id: callLogTarget.id,
        feedback: callLogData.feedback,
        expected_payment_date: callLogData.expected_payment_date,
        call_date: callLogData.call_date
      });
      toast.success('Call log saved');
      setCallLogTarget(null);
      setCallLogData({ feedback: '', expected_payment_date: '', call_date: new Date().toISOString().split('T')[0] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save call log');
    } finally {
      setSavingCallLog(false);
    }
  };

  const renderStudentFormFields = (data, onChange, isEdit = false, familySlot = null) => (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>First Name *</Label>
          <Input value={data.first_name} onChange={(e) => onChange({ ...data, first_name: e.target.value })} required data-testid="student-first-name" />
        </div>
        <div className="space-y-2">
          <Label>Last Name *</Label>
          <Input value={data.last_name} onChange={(e) => onChange({ ...data, last_name: e.target.value })} required data-testid="student-last-name" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Branch</Label>
          <Select value={data.branch} onValueChange={(v) => onChange({ ...data, branch: v })}>
            <SelectTrigger data-testid="student-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
            <SelectContent>
              {getAvailableBranches(config?.branches)?.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Class</Label>
          <Select value={data.current_class} onValueChange={(v) => onChange({ ...data, current_class: v })}>
            <SelectTrigger data-testid="student-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
            <SelectContent>
              {config?.classes?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {familySlot}
      <div className="space-y-2">
        <Label>Parent/Guardian Name *</Label>
        <Input value={data.parent_guardian} onChange={(e) => onChange({ ...data, parent_guardian: e.target.value })} required data-testid="student-parent-name" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Email</Label>
          <Input type="email" value={data.email} onChange={(e) => onChange({ ...data, email: e.target.value })} data-testid="student-email" />
        </div>
        <div className="space-y-2">
          <Label>Phone</Label>
          <Input value={data.phone} onChange={(e) => onChange({ ...data, phone: e.target.value })} data-testid="student-phone" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Date of Joining</Label>
          <Input
            type="date"
            value={data.date_of_joining || ''}
            onChange={(e) => onChange({ ...data, date_of_joining: e.target.value })}
            data-testid="student-date-of-joining"
          />
          <p className="text-xs text-[#8A8E8B]">Sets the admission term on the dashboard. Blank = today.</p>
        </div>
      </div>
    </>
  );

  // Discount percentage fields (admin-only) shown in the edit dialog.
  const DISCOUNT_FIELDS = [
    { key: 'discount_ismaili', label: 'Ismaili %' },
    { key: 'discount_pak_assoc', label: 'Pak Assoc %' },
    { key: 'discount_special', label: 'Special %' },
    { key: 'discount_sibling', label: 'Sibling %' },
    { key: 'discount_yearly', label: 'Yearly %' },
    { key: 'discount_benefit', label: 'Benefit %' },
  ];

  const renderEditFormFields = (data, onChange) => (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>First Name *</Label>
          <Input value={data.first_name} onChange={(e) => onChange({ ...data, first_name: e.target.value })} required data-testid="edit-first-name" />
        </div>
        <div className="space-y-2">
          <Label>Last Name *</Label>
          <Input value={data.last_name} onChange={(e) => onChange({ ...data, last_name: e.target.value })} required data-testid="edit-last-name" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Branch</Label>
          <Select value={data.branch} onValueChange={(v) => onChange({ ...data, branch: v })}>
            <SelectTrigger data-testid="edit-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
            <SelectContent>
              {getAvailableBranches(config?.branches)?.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Class</Label>
          <Select value={data.current_class} onValueChange={(v) => onChange({ ...data, current_class: v })}>
            <SelectTrigger data-testid="edit-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
            <SelectContent>
              {config?.classes?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Parent/Guardian Name *</Label>
        <Input value={data.parent_guardian} onChange={(e) => onChange({ ...data, parent_guardian: e.target.value })} required data-testid="edit-parent-name" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Email</Label>
          <Input type="email" value={data.parent_email} onChange={(e) => onChange({ ...data, parent_email: e.target.value })} data-testid="edit-parent-email" />
          <p className="text-xs text-[#8A8E8B]">Used for invoice &amp; receipt emails.</p>
        </div>
        <div className="space-y-2">
          <Label>Parent Phone</Label>
          <Input value={data.parent_phone} onChange={(e) => onChange({ ...data, parent_phone: e.target.value })} data-testid="edit-parent-phone" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Date of Joining</Label>
          <Input
            type="date"
            value={data.date_of_joining || ''}
            onChange={(e) => onChange({ ...data, date_of_joining: e.target.value })}
            data-testid="edit-date-of-joining"
          />
          <p className="text-xs text-[#8A8E8B]">
            Drives the New Admissions term on the dashboard.
            {data.joining_source && data.joining_source !== 'manual' && (
              <> Currently derived from {String(data.joining_source).replace('_', ' ')} — saving a date confirms it.</>
            )}
          </p>
        </div>
      </div>

      {isAdmin && (
        <div className="pt-4 mt-2 border-t border-[#D8D9E8] space-y-4">
          <p className="text-xs font-semibold text-[#5C635F] uppercase tracking-wider">Admin Only</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Student ID</Label>
              <Input value={data.student_id} onChange={(e) => onChange({ ...data, student_id: e.target.value })} data-testid="edit-student-id" />
            </div>
            <div className="space-y-2">
              <Label>Academic Year</Label>
              <Select value={data.academic_year} onValueChange={(v) => onChange({ ...data, academic_year: v })}>
                <SelectTrigger data-testid="edit-academic-year"><SelectValue placeholder="Select year" /></SelectTrigger>
                <SelectContent>
                  {(config?.academic_years || ['2024-2025', '2025-2026', '2026-2027']).map((y) => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium text-[#5C635F]">Discounts</Label>
            <div className="grid grid-cols-3 gap-3 mt-2">
              {DISCOUNT_FIELDS.map(({ key, label }) => (
                <div key={key} className="space-y-1">
                  <Label className="text-xs">{label}</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="any"
                    value={data[key]}
                    onChange={(e) => onChange({ ...data, [key]: e.target.value === '' ? 0 : Number(e.target.value) })}
                    data-testid={`edit-${key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1F1D]">Students</h1>
          <p className="text-[#5C635F] mt-1">{total} students enrolled</p>
        </div>
        
        <div className="flex items-center gap-2">
        {/* Selection actions live in the floating bar at the bottom (see below) so
            ticking a row never reflows this header. */}
        {canAddStudent && (
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#3E3F96] hover:bg-[#2D2E7A]" data-testid="add-student-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Student
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Student</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddStudent} className="space-y-4">
                {renderStudentFormFields(newStudent, setNewStudent, false, canUseFamilies ? (
                  <div className="space-y-2 p-3 bg-[#F5F5FA] rounded-lg border border-[#D8D9E8]">
                    <Label>Family (optional)</Label>
                    <Select value={newStudent.family_code || 'none'} onValueChange={(v) => onSelectFamily(v === 'none' ? '' : v)}>
                      <SelectTrigger data-testid="student-family-select"><SelectValue placeholder="Link to a family" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— No family —</SelectItem>
                        {families.map((f) => (
                          <SelectItem key={f.family_code} value={f.family_code}>
                            {f.family_code} · {f.family_name}{f.branch ? ` (${f.branch})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {newStudent.family_code && (() => {
                      const fam = families.find((f) => f.family_code === newStudent.family_code);
                      const ds = fam?.auto_discounts || [];
                      return (
                        <>
                          {fam?.guardian_display_name && <p className="text-xs text-[#5C635F]">Guardian auto-filled: <span className="font-medium">{fam.guardian_display_name}</span>{fam.guardian_display_phone ? ` · ${fam.guardian_display_phone}` : ''} (editable below)</p>}
                          {ds.length > 0
                            ? <p className="text-xs text-[#5C635F]">Will request (pending approval): {ds.map((d) => `${d.discount_type.replace(/_/g, ' ')} ${d.percentage ? d.percentage + '%' : ''}`).join(', ')}</p>
                            : <p className="text-xs text-[#9CA3AF]">This family has no auto-discounts.</p>}
                        </>
                      );
                    })()}
                  </div>
                ) : null)}
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={submitting} data-testid="submit-student-btn">
                    {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Add Student
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* Lifecycle Tabs + KPI Term filter */}
      <div className="flex items-center justify-between gap-2 border-b border-[#D8D9E8] flex-wrap">
        <div className="flex items-center gap-1">
          {[
            { key: 'live', label: 'Live' },
            { key: 'qualified', label: 'Qualified' },
            { key: 'left', label: 'Left' },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => changeLifecycleTab(t.key)}
              className={`px-5 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                lifecycleTab === t.key
                  ? 'border-[#3E3F96] text-[#3E3F96]'
                  : 'border-transparent text-[#5C635F] hover:text-[#3E3F96]'
              }`}
              data-testid={`lifecycle-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 pb-2">
          <span className="text-xs text-[#5C635F]">KPI Term</span>
          <Select value={kpiTerm} onValueChange={setKpiTerm}>
            <SelectTrigger className="w-36" data-testid="kpi-term-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Terms</SelectItem>
              {(config?.terms || ['Term 1', 'Term 2', 'Term 3']).map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {lifecycleTab === 'qualified' && (
        <div className="text-sm text-[#5C635F] bg-[#F5F5FA] border border-[#D8D9E8] rounded-lg px-4 py-3">
          To qualify students, go to <span className="font-medium">Settings → Academic → Batch Promotion</span> and promote them with <span className="font-medium">To Class = Qualified</span>. They will then appear in this tab.
        </div>
      )}

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-[#D8D9E8]">
          <CardContent className="p-4">
            <p className="text-xs text-[#5C635F] uppercase tracking-wide">Total Students</p>
            <p className="text-2xl font-bold text-[#1C1F1D]" data-testid="kpi-total-students">{kpiSummary.totalStudents.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-[#D8D9E8]">
          <CardContent className="p-4">
            <p className="text-xs text-[#5C635F] uppercase tracking-wide">
              Opening Balance{kpiTerm !== 'all' ? ` (${kpiTerm})` : ''}
            </p>
            <p className="text-2xl font-bold text-[#3E3F96]" data-testid="kpi-opening-balance">
              USD {kpiSummary.totalOpeningBalance.toLocaleString()}
            </p>
          </CardContent>
        </Card>
        {canViewOutstanding && (
          <Card className="border-[#D8D9E8]">
            <CardContent className="p-4">
              <p className="text-xs text-[#5C635F] uppercase tracking-wide">
                {kpiTerm !== 'all' ? `Net Invoice Outstanding (${kpiTerm})` : 'Invoice Outstanding'}
              </p>
              <p className="text-2xl font-bold text-[#C85A40]" data-testid="kpi-invoice-outstanding">
                USD {kpiSummary.totalInvoiceOutstanding.toLocaleString()}
              </p>
            </CardContent>
          </Card>
        )}
        {canViewOutstanding && (
          <Card className="border-[#D8D9E8]">
            <CardContent className="p-4">
              <p className="text-xs text-[#5C635F] uppercase tracking-wide">Total Overdue</p>
              <p className="text-2xl font-bold text-[#D99C3B]" data-testid="kpi-total-overdue">
                USD {kpiSummary.totalOverdue.toLocaleString()}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Filters */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#5C635F]" />
              <Input
                placeholder="Search by name or ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="student-search-input"
              />
            </div>
            <MultiSelect widthClass="w-full sm:w-40" placeholder="All Branches" testId="branch-filter"
              options={getAvailableBranches(config?.branches) || []}
              selected={branchFilter} onChange={(v) => { setBranchFilter(v); setPage(1); }} />
            <MultiSelect widthClass="w-full sm:w-40" placeholder="All Classes" testId="class-filter"
              options={config?.classes || []}
              selected={classFilter} onChange={(v) => { setClassFilter(v); setPage(1); }} />
            <MultiSelect widthClass="w-full sm:w-36" placeholder="All Status" testId="status-filter"
              options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
              selected={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(1); }} />
            <Button type="submit" variant="outline" data-testid="search-btn">
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Students table */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#3E3F96]" />
            </div>
          ) : students.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-[#5C635F]">
              <User className="w-12 h-12 mb-4 opacity-50" />
              <p>No students found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F5FA] hover:bg-[#F5F5FA]">
                    {canManageStudents && (
                      <TableHead className="w-10">
                        <Checkbox
                          checked={students.length > 0 && selectedIds.size === students.length}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                    )}
                    <TableHead onClick={() => handleSort('student_id')} className="text-xs uppercase font-semibold text-[#5C635F] cursor-pointer select-none hover:text-[#3E3F96]">
                      Student ID<SortIcon field="student_id" />
                    </TableHead>
                    <TableHead onClick={() => handleSort('first_name')} className="text-xs uppercase font-semibold text-[#5C635F] cursor-pointer select-none hover:text-[#3E3F96]">
                      Name<SortIcon field="first_name" />
                    </TableHead>
                    <TableHead onClick={() => handleSort('current_class')} className="text-xs uppercase font-semibold text-[#5C635F] cursor-pointer select-none hover:text-[#3E3F96]">
                      Class<SortIcon field="current_class" />
                    </TableHead>
                    <TableHead onClick={() => handleSort('branch')} className="text-xs uppercase font-semibold text-[#5C635F] cursor-pointer select-none hover:text-[#3E3F96]">
                      Branch<SortIcon field="branch" />
                    </TableHead>
                    <TableHead onClick={() => handleSort('parent_guardian')} className="text-xs uppercase font-semibold text-[#5C635F] cursor-pointer select-none hover:text-[#3E3F96]">
                      Parent/Guardian<SortIcon field="parent_guardian" />
                    </TableHead>
                    <TableHead onClick={() => handleSort('status')} className="text-xs uppercase font-semibold text-[#5C635F] cursor-pointer select-none hover:text-[#3E3F96]">
                      Status<SortIcon field="status" />
                    </TableHead>
                    <TableHead onClick={() => handleSort('opening_balance')} className="text-xs uppercase font-semibold text-[#5C635F] text-right cursor-pointer select-none hover:text-[#3E3F96]">
                      Opening Bal<SortIcon field="opening_balance" />
                    </TableHead>
                    {canViewOutstanding && (
                      <TableHead onClick={() => handleSort('closing_balance')} className="text-xs uppercase font-semibold text-[#5C635F] text-right cursor-pointer select-none hover:text-[#3E3F96]">
                        Closing Balance<SortIcon field="closing_balance" />
                      </TableHead>
                    )}
                    {lifecycleTab === 'left' && (
                      <TableHead className="text-xs uppercase font-semibold text-[#5C635F]">Left Date</TableHead>
                    )}
                    <TableHead className="text-xs uppercase font-semibold text-[#5C635F] text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.map((student) => (
                    <TableRow
                      key={student.id}
                      className="hover:bg-[#F5F5FA]"
                      data-testid={`student-row-${student.student_id}`}
                    >
                      {canManageStudents && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(student.id)}
                            onCheckedChange={() => toggleSelect(student.id)}
                            data-testid={`select-student-${student.student_id}`}
                          />
                        </TableCell>
                      )}
                      <TableCell 
                        className="font-medium text-[#3E3F96] cursor-pointer"
                        onClick={() => navigate(`/students/${student.id}`)}
                      >
                        {student.student_id}
                      </TableCell>
                      <TableCell 
                        className="cursor-pointer"
                        onClick={() => navigate(`/students/${student.id}`)}
                      >
                        <div className="font-medium text-[#1C1F1D]">
                          {student.first_name} {student.last_name}
                        </div>
                        <div className="text-sm text-[#5C635F]">{student.parent_email || student.email}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="bg-[#ECEDF5] text-[#3E3F96]">
                          {student.current_class}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[#5C635F]">{student.branch}</TableCell>
                      <TableCell className="text-[#5C635F]">
                        <div>{student.parent_guardian}</div>
                        {student.phone && <div className="text-xs text-[#8A8E8B] mt-0.5">{student.phone}</div>}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="secondary" 
                          className={
                            (student.status || 'active') === 'active'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-600'
                          }
                          data-testid={`status-badge-${student.student_id}`}
                        >
                          {(student.status || 'active').charAt(0).toUpperCase() + (student.status || 'active').slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className={`text-right font-medium tabular-nums ${getBalanceColor(student.opening_balance || student.balance)}`}>
                        {formatCurrency(student.opening_balance ?? student.balance)}
                      </TableCell>
                      {canViewOutstanding && (
                        <TableCell className={`text-right font-bold tabular-nums ${getBalanceColor(student.closing_balance ?? student.effective_outstanding ?? 0)}`}>
                          {formatCurrency(student.closing_balance ?? student.effective_outstanding ?? 0)}
                        </TableCell>
                      )}
                      {lifecycleTab === 'left' && (
                        <TableCell className="text-[#5C635F] text-sm">{formatDate(student.left_date)}</TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          {canEditStudent(student) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => openEditDialog(student, e)}
                              title="Edit"
                              data-testid={`edit-student-${student.student_id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                          {canManageStudents && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleToggleStatus(student, e)}
                              title={(student.status || 'active') === 'active' ? 'Deactivate' : 'Activate'}
                              data-testid={`toggle-status-${student.student_id}`}
                            >
                              <Power className={`w-4 h-4 ${(student.status || 'active') === 'active' ? 'text-green-600' : 'text-red-500'}`} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); setCallLogTarget(student); setCallLogData({ feedback: '', expected_payment_date: '', call_date: new Date().toISOString().split('T')[0] }); }}
                            title="Log Call"
                            className="text-[#3E3F96]"
                            data-testid={`log-call-${student.student_id}`}
                          >
                            <Phone className="w-4 h-4" />
                          </Button>
                          {canManageStudents && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); setDeleteTarget(student); }}
                              title="Delete"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-student-${student.student_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#5C635F]">
            Showing {((page - 1) * 25) + 1} - {Math.min(page * 25, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="prev-page-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-[#5C635F]">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="next-page-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit Student Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Student</DialogTitle>
          </DialogHeader>
          {editingStudent && (
            <form onSubmit={handleEditStudent} className="space-y-4">
              <div className="p-2 bg-[#F5F5FA] rounded text-sm text-[#5C635F]">
                Student ID: <span className="font-medium text-[#3E3F96]">{editingStudent.student_id}</span>
              </div>
              {renderEditFormFields(editData, setEditData)}
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={submitting} data-testid="save-edit-student-btn">
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={showBulkEditDialog} onOpenChange={setShowBulkEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Edit {selectedIds.size} Students</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-[#D99C3B]/10 border border-[#D99C3B]/20 rounded-lg text-sm text-[#D99C3B]">
              Only filled fields will be updated. Leave empty to skip a field.
            </div>
            <div className="space-y-2">
              <Label>Branch</Label>
              <Select value={bulkEditData.branch} onValueChange={(v) => setBulkEditData({ ...bulkEditData, branch: v })}>
                <SelectTrigger data-testid="bulk-branch-select"><SelectValue placeholder="No change" /></SelectTrigger>
                <SelectContent>
                  {getAvailableBranches(config?.branches)?.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Class</Label>
              <Select value={bulkEditData.current_class} onValueChange={(v) => setBulkEditData({ ...bulkEditData, current_class: v })}>
                <SelectTrigger data-testid="bulk-class-select"><SelectValue placeholder="No change" /></SelectTrigger>
                <SelectContent>
                  {config?.classes?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowBulkEditDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleBulkEdit}
                className="bg-[#3E3F96] hover:bg-[#2D2E7A]"
                disabled={bulkSubmitting}
                data-testid="submit-bulk-edit-btn"
              >
                {bulkSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Update {selectedIds.size} Students
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Student</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.first_name} {deleteTarget?.last_name}</strong> ({deleteTarget?.student_id})?
              This action cannot be undone. Students with outstanding invoices cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteStudent}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Call Log Dialog */}
      <Dialog open={!!callLogTarget} onOpenChange={(open) => { if (!open) setCallLogTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Log Call - {callLogTarget?.first_name} {callLogTarget?.last_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveCallLog} className="space-y-4">
            <div className="p-3 bg-[#F5F5FA] rounded-lg text-sm">
              <span className="text-[#5C635F]">Student: </span>
              <span className="font-medium text-[#3E3F96]">{callLogTarget?.student_id}</span>
              <span className="text-[#5C635F]"> | {callLogTarget?.branch} - {callLogTarget?.current_class}</span>
            </div>
            <div className="space-y-2">
              <Label>Call Date</Label>
              <Input type="date" value={callLogData.call_date} onChange={e => setCallLogData({ ...callLogData, call_date: e.target.value })} data-testid="call-log-date" />
            </div>
            <div className="space-y-2">
              <Label>Feedback / Comments</Label>
              <Textarea placeholder="Enter call feedback..." value={callLogData.feedback} onChange={e => setCallLogData({ ...callLogData, feedback: e.target.value })}
                rows={3} data-testid="call-log-feedback" />
            </div>
            <div className="space-y-2">
              <Label>Expected Payment Date</Label>
              <Input type="date" value={callLogData.expected_payment_date} onChange={e => setCallLogData({ ...callLogData, expected_payment_date: e.target.value })} data-testid="call-log-payment-date" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCallLogTarget(null)}>Cancel</Button>
              <Button type="submit" className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={savingCallLog || !callLogData.feedback} data-testid="save-call-log-btn">
                {savingCallLog && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Call Log
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Floating selection bar — same pattern as Payments/Invoices/Credit Notes/
          Bank Rec, so selecting students never moves the page under the cursor. */}
      <BulkSelectionBar
        count={selectedIds.size}
        label={selectedIds.size === 1 ? 'student selected' : 'students selected'}
        onClear={() => setSelectedIds(new Set())}
      >
        {canManageStudents && (
          <Button variant="outline" size="sm" onClick={() => setShowBulkEditDialog(true)}
            data-testid="bulk-edit-btn">
            <Users className="w-4 h-4 mr-2" />Bulk Edit
          </Button>
        )}
        {canManageLifecycle && lifecycleTab === 'live' && (
          <Button variant="outline" size="sm" onClick={() => markLifecycle('left')}
            disabled={lifecycleSubmitting}
            className="text-[#C85A40] border-[#C85A40]/40 hover:bg-[#C85A40]/10"
            data-testid="mark-left-btn">
            <LogOut className="w-4 h-4 mr-2" />Mark Left
          </Button>
        )}
        {canManageLifecycle && lifecycleTab !== 'live' && (
          <Button variant="outline" size="sm" onClick={() => markLifecycle('live')}
            disabled={lifecycleSubmitting} data-testid="move-live-btn">
            <RotateCcw className="w-4 h-4 mr-2" />Move to Live
          </Button>
        )}
      </BulkSelectionBar>
    </div>
  );
};

export default StudentsPage;
